/**
 * <s-offer deadline="ISO-8601"> — fixed-deadline offer state for the landing page.
 *
 * Writes data-offer="live|ended" on <html> (the offer-live:/offer-ended: variants in
 * config/styles.css key off it), paints every [data-cd] countdown in the document, and
 * makes [data-gift] regions inert once the offer has ended. After the offer ends, any
 * a[href="#join_pkg"] click draws attention to the one-tube card ([data-offer-card="one"]).
 *
 * Countdown markup: [data-cd] containing [data-cd-d], [data-cd-h], [data-cd-m], [data-cd-s].
 */
class SOffer extends HTMLElement {
  #deadline = 0;
  #timer = 0;
  #state = '';
  // Printed copy still says October 18; #label() swaps each form for the real end date.
  #shown = { up: 'OCTOBER 18', mixed: 'October 18', full: 'Sunday, October 18, 2026' };
  #ac;

  connectedCallback() {
    this.#deadline = this.#roll();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    document.addEventListener('click', (e) => this.#onClick(e), { signal });
    document.addEventListener('visibilitychange', () => this.#tick(), { signal });
    addEventListener('pageshow', () => this.#tick(), { signal });
    this.#tick();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  // Deadline = 23:59:59 local time, 3 days after first visit, kept in localStorage; once it passes, a fresh one starts.
  #roll() {
    const key = 'fv-offer-deadline';
    let end = 0;
    try { end = Number(localStorage.getItem(key)); } catch {}
    if (!(end > Date.now())) {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      d.setHours(23, 59, 59, 999);
      end = d.getTime();
      try { localStorage.setItem(key, String(end)); } catch {}
    }
    return end;
  }

  // Swap every printed end date ("ENDS OCTOBER 18", "October 18", "Sunday, October 18, 2026") for the real one.
  #label() {
    const d = new Date(this.#deadline - 6e4);
    const mixed = d.toLocaleString('en-US', { month: 'long', day: 'numeric' });
    const next = {
      up: mixed.toUpperCase(),
      mixed,
      full: d.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
    };
    if (next.up === this.#shown.up) return;
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      let v = n.nodeValue;
      for (const k of ['full', 'mixed', 'up']) v = v.replaceAll(this.#shown[k], next[k]);
      if (v !== n.nodeValue) n.nodeValue = v;
    }
    this.#shown = next;
  }

  #tick() {
    clearTimeout(this.#timer);
    if (this.#deadline - Date.now() <= 0) this.#deadline = this.#roll();
    this.#label();
    const rem = this.#deadline - Date.now();
    const state = rem > 0 ? 'live' : 'ended';
    if (state !== this.#state) {
      this.#state = state;
      document.documentElement.dataset.offer = state;
      for (const el of document.querySelectorAll('[data-gift]')) {
        el.inert = state !== 'live';
      }
    }
    this.#paint(Math.max(0, rem));
    // Wake on the next whole-second boundary (and exactly at expiry).
    const delay = rem > 0 ? (rem % 1000 || 1000) + 3 : 0;
    if (delay) this.#timer = setTimeout(() => this.#tick(), delay);
  }

  #paint(rem) {
    const v = {
      d: Math.floor(rem / 864e5),
      h: Math.floor((rem % 864e5) / 36e5),
      m: Math.floor((rem % 36e5) / 6e4),
      s: Math.floor((rem % 6e4) / 1e3),
    };
    for (const cd of document.querySelectorAll('[data-cd]')) {
      for (const k of ['d', 'h', 'm', 's']) {
        const el = cd.querySelector(`[data-cd-${k}]`);
        if (el && el.textContent !== String(v[k])) el.textContent = String(v[k]);
      }
    }
  }

  #onClick(e) {
    if (this.#state !== 'ended') return;
    if (!e.target.closest('a[href="#join_pkg"]')) return;
    const card = document.querySelector('[data-offer-card="one"]');
    if (!card) return;
    card.setAttribute('data-attn', '');
    setTimeout(() => card.removeAttribute('data-attn'), 3200);
    setTimeout(() => document.querySelector('[data-select="one"]')?.focus({ preventScroll: true }), 120);
  }
}

customElements.define('s-offer', SOffer);
